import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useState, useMemo } from "react";
import { Flame, Moon, Brain, Heart, Activity, Droplets } from "lucide-react";

const sleepQualityIcon = "/sleep-quality-icon.png";
const hotFlashesIcon = "/hot-flashes-icon.png";
const brainFogIcon = "/brain-fog-icon.png";
const moodIcon = "/mood-icon.png";
const energyLevelsIcon = "/energy-levels-icon.png";
const hydrationIcon = "/hydration-icon.png";

interface SymptomLog {
  log_date: string;
  symptom_type: string;
  intensity: number;
}

interface MonthlyHeatmapProps {
  symptomLogs: SymptomLog[];
  onSymptomChange?: (symptomType: string) => void;
}

const symptoms = [
  { id: "hotflash", label: "Hot Flashes", icon: Flame, image: hotFlashesIcon },
  { id: "sleep", label: "Sleep Quality", icon: Moon, image: sleepQualityIcon },
  { id: "brainfog", label: "Brain Fog", icon: Brain, image: brainFogIcon },
  { id: "mood", label: "Mood", icon: Heart, image: moodIcon },
  { id: "energy", label: "Energy Level", icon: Activity, image: energyLevelsIcon },
  { id: "hydration", label: "Hydration", icon: Droplets, image: hydrationIcon },
];

const renderSymptomIcon = (symptom: typeof symptoms[0], className?: string) => {
  if (symptom.image) {
    return <img src={symptom.image} alt={symptom.label} className={`object-cover rounded-full ${className}`} />;
  }
  const Icon = symptom.icon;
  return <Icon className={className} />;
};

const MonthlyHeatmap = ({ symptomLogs, onSymptomChange }: MonthlyHeatmapProps) => {
  const [selectedSymptom, setSelectedSymptom] = useState("hotflash");
  
  const handleSymptomChange = (symptom: string) => {
    setSelectedSymptom(symptom);
    onSymptomChange?.(symptom);
  };
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Get intensity color based on value
  const getIntensityColor = (intensity: number) => {
    if (intensity === 0) return "bg-muted/30";
    if (intensity <= 2) return "bg-green-200 dark:bg-green-900/40";
    if (intensity <= 4) return "bg-yellow-200 dark:bg-yellow-900/40";
    if (intensity <= 6) return "bg-orange-200 dark:bg-orange-900/40";
    if (intensity <= 8) return "bg-red-200 dark:bg-red-900/40";
    return "bg-red-400 dark:bg-red-800/60";
  };

  // Build calendar data for selected month
  const calendarData = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    // Filter logs for selected symptom and month
    const filteredLogs = symptomLogs.filter(log => {
      const logDate = new Date(log.log_date);
      return log.symptom_type === selectedSymptom &&
             logDate.getMonth() === month - 1 &&
             logDate.getFullYear() === year;
    });

    // Create intensity map with averages for multiple episodes per day
    const intensityMap: Record<number, { avgIntensity: number; episodeCount: number }> = {};
    filteredLogs.forEach(log => {
      const day = new Date(log.log_date).getDate();
      if (!intensityMap[day]) {
        intensityMap[day] = { avgIntensity: 0, episodeCount: 0 };
      }
      intensityMap[day].avgIntensity += log.intensity;
      intensityMap[day].episodeCount += 1;
    });
    
    // Calculate averages
    Object.keys(intensityMap).forEach(day => {
      const dayNum = parseInt(day);
      intensityMap[dayNum].avgIntensity = Math.round(
        intensityMap[dayNum].avgIntensity / intensityMap[dayNum].episodeCount
      );
    });

    // Build calendar grid
    const calendar: Array<{ day: number | null; intensity: number; episodeCount?: number }> = [];
    
    // Add empty cells for days before the 1st
    for (let i = 0; i < startDayOfWeek; i++) {
      calendar.push({ day: null, intensity: 0, episodeCount: 0 });
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      calendar.push({
        day,
        intensity: intensityMap[day]?.avgIntensity || 0,
        episodeCount: intensityMap[day]?.episodeCount || 0
      });
    }

    return calendar;
  }, [selectedMonth, selectedSymptom, symptomLogs]);

  // Get available months from data
  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    symptomLogs.forEach(log => {
      const date = new Date(log.log_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.add(monthKey);
    });
    return Array.from(months).sort().reverse();
  }, [symptomLogs]);

  const selectedSymptomData = symptoms.find(s => s.id === selectedSymptom);

  return (
    <Card className="mb-8">
      <CardHeader className="pb-3">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              {selectedSymptomData && (
                selectedSymptomData.image ? (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-primary-soft">
                    {renderSymptomIcon(selectedSymptomData, "w-full h-full")}
                  </div>
                ) : (
                  renderSymptomIcon(selectedSymptomData, "h-5 w-5 text-primary")
                )
              )}
              Monthly Symptom Heatmap
            </CardTitle>
            <CardDescription>
              Visualize symptom intensity patterns throughout the month to spot cycles and trends.
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={selectedSymptom} onValueChange={handleSymptomChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {symptoms.map(symptom => (
                  <SelectItem key={symptom.id} value={symptom.id}>
                    {symptom.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableMonths.map(month => {
                  const [year, monthNum] = month.split('-');
                  const date = new Date(parseInt(year), parseInt(monthNum) - 1);
                  return (
                    <SelectItem key={month} value={month}>
                      {date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {/* Usage Instructions */}
        <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border/50">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <strong className="text-foreground">How to use:</strong> Select a symptom and month above to see your daily intensity patterns. 
            Hover over colored days for exact levels. Look for recurring patterns (like weekly cycles or monthly trends) that can help you 
            understand your symptom rhythms and track improvements over time.
          </p>
        </div>
        {/* Calendar Grid */}
        <div className="space-y-2">
          {/* Day labels */}
          <div className="grid grid-cols-7 gap-2 text-center text-xs font-medium text-muted-foreground">
            <div>Sun</div>
            <div>Mon</div>
            <div>Tue</div>
            <div>Wed</div>
            <div>Thu</div>
            <div>Fri</div>
            <div>Sat</div>
          </div>

          {/* Calendar days */}
          <TooltipProvider delayDuration={100}>
            <div className="grid grid-cols-7 gap-2">
              {calendarData.map((cell, idx) => {
                const hasIntensity = cell.day && cell.intensity > 0;
                
                return hasIntensity ? (
                  <Tooltip key={idx}>
                    <TooltipTrigger asChild>
                      <div
                        className={`
                          aspect-[4/3] flex items-center justify-center rounded-sm text-xs font-medium
                          transition-all duration-200 cursor-pointer hover:scale-110 hover:shadow-md
                          ${getIntensityColor(cell.intensity)}
                          border border-border/50
                        `}
                      >
                        <span className={cell.intensity > 6 ? 'text-white font-bold' : 'text-foreground'}>
                          {cell.day}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-popover text-popover-foreground border-border z-50">
                      <p className="font-semibold">Day {cell.day}</p>
                      <p className="text-sm">Episodes: {cell.episodeCount}</p>
                      <p className="text-sm">Avg Intensity: {cell.intensity}/10</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div
                    key={idx}
                    className={`
                      aspect-[4/3] flex items-center justify-center rounded-sm text-xs font-medium
                      ${cell.day ? getIntensityColor(cell.intensity) : 'bg-transparent'}
                      ${cell.day ? 'border border-border/50' : ''}
                    `}
                  >
                    {cell.day && (
                      <span className="text-foreground">
                        {cell.day}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </TooltipProvider>

          {/* Legend */}
          <div className="flex items-center justify-center gap-2 pt-3 border-t text-[10px]">
            <span className="text-muted-foreground">Intensity:</span>
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-muted/30 border border-border/50"></div>
                <span>0</span>
              </div>
              <div className="flex items-center gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-green-200 dark:bg-green-900/40 border border-border/50"></div>
                <span>1-2</span>
              </div>
              <div className="flex items-center gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-yellow-200 dark:bg-yellow-900/40 border border-border/50"></div>
                <span>3-4</span>
              </div>
              <div className="flex items-center gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-orange-200 dark:bg-orange-900/40 border border-border/50"></div>
                <span>5-6</span>
              </div>
              <div className="flex items-center gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-red-200 dark:bg-red-900/40 border border-border/50"></div>
                <span>7-8</span>
              </div>
              <div className="flex items-center gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-red-400 dark:bg-red-800/60 border border-border/50"></div>
                <span>9-10</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlyHeatmap;
